
/**
 * Utilities for stylesheet management and refresh
 */
import { toast } from "sonner";
import { pulseUIRefresh } from "./refreshUtils";

/**
 * Reload all stylesheets with cache-busting
 */
export function reloadStylesheets(): void {
  const timestamp = Date.now();
  
  // Set a timestamp CSS variable that will force recalculation of styles
  document.documentElement.style.setProperty('--cache-timestamp', `${timestamp}`);
  
  // Apply a visual effect to indicate refresh
  pulseUIRefresh();
  
  document.querySelectorAll('link[rel="stylesheet"]').forEach(link => {
    const href = link.getAttribute('href');
    if (href) {
      // Create new href with cache busting
      const newHref = href.includes('?') 
        ? `${href}&cache=${timestamp}` 
        : `${href}?cache=${timestamp}`;
      
      // Create and insert new stylesheet
      const newLink = document.createElement('link');
      newLink.rel = 'stylesheet';
      newLink.href = newHref;
      document.head.appendChild(newLink);
      
      // Remove old stylesheet after new one loads
      newLink.onload = () => {
        if (link.parentNode) {
          link.parentNode.removeChild(link);
        }
      };
    }
  });
}

/**
 * Aggressively refresh stylesheets by adding timestamp parameters
 */
export function forceRefreshStylesheets(): void {
  try {
    const linkElements = document.querySelectorAll('link[rel="stylesheet"]');
    const timestamp = Date.now();
    const versionId = window.__VERSION_ID__ || timestamp.toString();
    
    // Set a global timestamp CSS variable that will force recalculation
    document.documentElement.style.setProperty('--cache-timestamp', `${timestamp}`);
    document.documentElement.style.setProperty('--force-refresh', '1');
    
    linkElements.forEach(link => {
      // Create a new link with timestamp to force reload
      const newLink = document.createElement('link');
      newLink.rel = 'stylesheet';
      const originalHref = link.getAttribute('href') || '';
      
      // Add cache busting parameter
      newLink.href = originalHref.includes('?') 
        ? `${originalHref}&t=${timestamp}&v=${versionId}` 
        : `${originalHref}?t=${timestamp}&v=${versionId}`;
      
      document.head.appendChild(newLink);
      
      // Remove old link after new one loads
      newLink.onload = () => link.parentNode?.removeChild(link);
    });
    
    // Add a tiny amount of CSS directly to ensure refresh
    const style = document.createElement('style');
    style.textContent = `:root { --refresh-id: ${timestamp}; }`;
    document.head.appendChild(style);
    
    // Set a flag in session storage to indicate styles were refreshed
    sessionStorage.setItem('styles_refreshed_at', timestamp.toString());
  } catch (e) {
    console.error("Error refreshing stylesheets:", e);
  }
}

/**
 * Special login page refresh to ensure consistent styling
 */
export function refreshLoginPageStyles(): void {
  console.log("Refreshing login page styles to ensure consistency");

  // Set a flag to indicate styles are being refreshed
  sessionStorage.setItem('login-styles-refreshed', 'true');
  
  // Force remove and reapply all stylesheets
  forceRefreshStylesheets();
  
  // Specific forced style reload for tailwind classes
  const styleRefreshElement = document.createElement('div');
  styleRefreshElement.id = 'style-refresh-trigger';
  styleRefreshElement.style.display = 'none';
  document.body.appendChild(styleRefreshElement);
  
  // Apply and remove classes to force recalculation
  setTimeout(() => {
    styleRefreshElement.classList.add('login-style-refresh');
    setTimeout(() => {
      styleRefreshElement.classList.remove('login-style-refresh');
      if (styleRefreshElement.parentNode) {
        styleRefreshElement.parentNode.removeChild(styleRefreshElement);
      }
    }, 100);
  }, 50);
  
  // Add a temporary class to the body to ensure styling
  document.body.classList.add('login-page-view');
  
  // Add some custom CSS to force a repaint
  const style = document.createElement('style');
  const timestamp = Date.now();
  // Get version ID safely
  const versionId = typeof window !== 'undefined' && window.__VERSION_ID__ ? window.__VERSION_ID__ : '';
  
  style.textContent = `
    body.login-page-view {
      --login-refresh-timestamp: ${timestamp};
    }
    
    #style-refresh-trigger {
      opacity: 0;
      --refresh-time: ${timestamp};
    }
  `;
  document.head.appendChild(style);
  
  // Reload all CSS files with cache busting
  document.querySelectorAll('link[rel="stylesheet"]').forEach(link => {
    const href = link.getAttribute('href');
    if (href) {
      // Create a new link element with a cache-busting parameter
      const newLink = document.createElement('link');
      newLink.rel = 'stylesheet';
      newLink.type = 'text/css';
      
      // Add cache busting parameter
      const timestamp = Date.now();
      // Get version ID safely
      const vId = typeof window !== 'undefined' && window.__VERSION_ID__ ? window.__VERSION_ID__ : '';
      
      const newHref = href.includes('?') 
        ? `${href}&t=${timestamp}&v=${vId}&force=1` 
        : `${href}?t=${timestamp}&v=${vId}&force=1`;
      
      newLink.href = newHref;
      
      // Add the new link to the head
      document.head.appendChild(newLink);
      
      // Remove the old link after the new one has loaded
      newLink.onload = () => {
        if (link.parentNode) {
          link.parentNode.removeChild(link);
        }
      };
    }
  });
  
  toast.info("Refreshed page styling", {
    description: "If styles still look incorrect, please try a hard refresh"
  });
}
